// @ts-nocheck
/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import { resolveColor } from "@insite/mobius/utilities";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getSpacingPresetField, { spacingPresetOptions } from "@store/Framework/Common/Fields/Toolkit/spacingPreset";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
/* Store Custom Code - End */

const enum fields {
    backgroundColor = "backgroundColor",
    padding = "padding",
    content = "content",
    customCSS = "customCSS",
    // Starter Custom Code
    customSpacing = "customSpacing",
    fontSize = "fontSize",
    presetSpacing = "presetSpacing",
    showWrapper = "showWrapper",
    textVariant = "textVariant",
}

/* Starter Custom Code - Start */
const paddingField = getCssUnitField(fields.padding, { shorthands: ["top", "right", "bottom", "left"] });
const fontSizeField = getCssUnitField(fields.fontSize);
const presetSpacingField = getSpacingPresetField(fields.presetSpacing, { showCustom: true });
const customSpacingField = getCssUnitField(fields.customSpacing);
/* Starter Custom Code - End */

interface Props extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.padding]: string; // Starter Custom Code
        [fields.content]: string;
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.customSpacing]: string;
        [fields.fontSize]: string;
        [fields.presetSpacing]: string;
        [fields.showWrapper]: boolean;
        [fields.textVariant]: keyof ThemeTypography;
    };
}

export interface RichContentStyles {
    wrapper?: InjectableCss;
}

const richContentStyles: RichContentStyles /* Starter Custom Code */ = {
    wrapper: {},
};

/* Starter Custom Code - Start */
const defaultCustomCss = `& {
}`;
/* Starter Custom Code - End */

export const RichContent: React.FunctionComponent<Props> = props => {
    if (!props.fields.content) {
        return null;
    }

    const styles = useMergeStyles("richContent", richContentStyles);

    /* Starter Custom Code - Start */
    const {
        backgroundColor,
        content,
        customCSS,
        customSpacing,
        fontSize,
        padding,
        presetSpacing,
        showWrapper,
        textVariant,
    } = props.fields;

    const parsedContent = parse(content, parserOptions);
    const _showWrapper = typeof showWrapper === "boolean" ? showWrapper : Boolean(backgroundColor || padding); // Preserve legacy wrapper behavior

    if (!_showWrapper) {
        return <>{parsedContent}</>;
    }

    const customCssWrapper: TypographyPresentationProps = {
        variant: textVariant || undefined,
        forwardAs: "div",
        css: css`
            ${styles.wrapper?.css}
            ${breakpointValues(paddingField.parse(padding), "padding")}

            ${({ theme }) => css`
                ${cssProp("background-color", backgroundColor, x => resolveColor(x, theme))}

                ${() => {
                    if (!textVariant) {
                        return css`
                            ${breakpointValues(fontSizeField.parse(fontSize), "font-size")}

                            ${breakpointValues(
                                presetSpacingField.parse(presetSpacing, theme) ||
                                    customSpacingField.parse(customSpacing),
                                "margin-bottom",
                            )}

                            &:last-child {
                                margin-bottom: 0;
                            }
                        `;
                    }
                }}
            `}
            
            ${customCSS}
        `,
    };

    return (
        <Typography className="wrapper" {...customCssWrapper}>
            {parsedContent}
        </Typography>
    );
    /* Starter Custom Code - End */
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> /* Starter Custom Code */ = {
    component: RichContent,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            /* Starter Custom Code - Start */
            {
                name: fields.showWrapper,
                displayName: "Show Wrapper",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: null as any,
                tab: basicTab,
            },
            /* Starter Custom Code - End */
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "CustomColorPickerField", // Starter Custom Code
                displayName: "Background Color",
                defaultValue: "",
                tab: basicTab,
                isVisible: x => x.fields[fields.showWrapper], // Starter Custom Code
            },
            {
                ...paddingField.definition, // Starter Custom Code
                fieldType: "General",
                // Starter Removed - name
                // Starter Removed - editorTemplate
                displayName: "Content Padding",
                defaultValue: "", // Starter Custom Code
                tab: basicTab,
                isVisible: x => x.fields[fields.showWrapper], // Starter Custom Code
            },
            /* Starter Custom Code - Start */
            {
                name: fields.textVariant,
                displayName: "Text Variant",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "p" },
                    { displayName: "Banner", value: "bannerText" },
                    { displayName: "Custom", value: "" },
                ],
                defaultValue: "p",
                hideEmptyOption: true,
                isVisible: x => x.fields[fields.showWrapper],
            },
            {
                ...fontSizeField.definition,
                displayName: "Font Size",
                isVisible: x => x.fields[fields.showWrapper] && !x.fields[fields.textVariant],
            },
            {
                ...presetSpacingField.definition,
                defaultValue: spacingPresetOptions.content.id,
                isVisible: x => x.fields[fields.showWrapper] && !x.fields[fields.textVariant],
            },
            {
                ...customSpacingField.definition,
                isVisible: x =>
                    x.fields[fields.showWrapper] &&
                    !x.fields[fields.textVariant] &&
                    x.fields[fields.presetSpacing] === spacingPresetOptions.custom.id,
            },
            /* Starter Custom Code - End */
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) =>
                    !!shouldDisplayAdvancedFeatures && item.fields.showWrapper, // Starter Custom Code
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<{ backgroundColor: string; padding: number } & InjectableCss>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ padding }) => (padding ? `padding: ${padding}px;` : null)};
    ${widgetWrapperStyles.css /* Store Custom Code */}
    ${injectCss}
`;
